import React, { useState } from "react"
import {
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tabs,
  Tab,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import SummarizeIcon from "@mui/icons-material/Summarize"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import FactoryIcon from "@mui/icons-material/Factory"
import PersonIcon from "@mui/icons-material/Person"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import SendIcon from "@mui/icons-material/Send"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import HourglassTopIcon from "@mui/icons-material/HourglassTop"
import ThumbUpIcon from "@mui/icons-material/ThumbUp"
import RateReviewIcon from "@mui/icons-material/RateReview"
import LockIcon from "@mui/icons-material/Lock"
import { useTranslation } from "react-i18next"

const CAT1_LABELS = {
  Cabinet: "Κουτια",
  Accessories: "Αξεσουαρ",
  Special: "Ειδικα",
  Charges: "Χρεωσεις",
}

const CAT1_COLORS = {
  Cabinet: "#3b82f6",
  Accessories: "#d97706",
  Special: "#7c3aed",
  Charges: "#64748b",
}

const SECTION_COLORS = {
  Wall: "#2563eb",
  Base: "#16a34a",
  Tall: "#7c3aed",
}

const SECTION_LABELS = {
  Wall: "Κρεμαστα",
  Base: "Βασεως",
  Tall: "Ολοσωμα",
}

// ─── Cart Tab ────────────────────────────────────────────────
const CartTab = ({
  cart,
  groupedCart,
  cartTotal,
  calculateFinalPrice,
  onUpdateQty,
  onRemove,
  t,
}) => {
  const hasItems = cart.length > 0
  const totalQty = cart.reduce((sum, i) => sum + i.quantity, 0)

  if (!hasItems) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
          {t("Κενο καλαθι")}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
      {groupedCart.orderedCat1Keys.map((cat1Key, catIdx) => {
        const cat2Groups = groupedCart.groups[cat1Key]
        // Raw b2b total (no margin) for cart display
        const cat1Total = Object.values(cat2Groups).flat().reduce((sum, i) => sum + (parseFloat(i.b2bsalesprice) || 0) * i.quantity, 0)
        const label = CAT1_LABELS[cat1Key] || cat1Key
        const catColor = CAT1_COLORS[cat1Key] || "#64748b"

        return (
          <Box key={cat1Key} sx={{ mb: 1.5 }}>
            {catIdx > 0 && <Divider sx={{ mb: 1 }} />}

            {/* Category header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 3, height: 14, bgcolor: catColor, borderRadius: 1 }} />
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#1e293b", textTransform: "uppercase" }}>
                  {label}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: catColor }}>
                €{cat1Total.toFixed(2)}
              </Typography>
            </Stack>

            {/* Items per cat2 */}
            {Object.entries(cat2Groups).map(([cat2Key, items]) => (
              <Box key={cat2Key} sx={{ ml: 0.5, mb: 0.5 }}>
                {/* Cat2 subheader */}
                {Object.keys(cat2Groups).length > 1 && (
                  <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: SECTION_COLORS[cat2Key] || "#64748b", mb: 0.25 }}>
                    {SECTION_LABELS[cat2Key] || cat2Key}
                  </Typography>
                )}

                {items.map((item) => {
                  const unitPrice = parseFloat(item.b2bsalesprice) || 0
                  const lineTotal = unitPrice * item.quantity
                  return (
                    <Box
                      key={item.cartItemKey}
                      sx={{
                        py: 0.5,
                        px: 0.5,
                        borderRadius: 1,
                        transition: "all 0.1s",
                        "&:hover": { bgcolor: "#f8fafc" },
                        "&:hover .cart-actions": { opacity: 1 },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {item.swatch?.swatchUrl && (
                              <Box
                                component="img"
                                src={item.swatch.swatchUrl}
                                sx={{ width: 12, height: 12, borderRadius: "2px", objectFit: "cover", flexShrink: 0 }}
                                onError={(e) => { e.target.style.display = "none" }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                color: "#475569",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.name}
                            </Typography>
                          </Stack>
                          {item.sku && (
                            <Typography sx={{ fontSize: "0.5rem", fontFamily: "monospace", color: "#94a3b8", lineHeight: 1.1 }}>
                              {item.sku}
                            </Typography>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#1e293b", fontWeight: 600, whiteSpace: "nowrap" }}>
                          €{lineTotal.toFixed(2)}
                        </Typography>
                      </Stack>

                      {/* Qty stepper + remove */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 0.25 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={0}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (item.quantity <= 1) onRemove(item.cartItemKey)
                              else onUpdateQty(item.cartItemKey, -1)
                            }}
                            sx={{ width: 18, height: 18, border: "1px solid #e2e8f0", borderRadius: 0.5, "&:hover": { borderColor: "#ef4444" } }}
                          >
                            <RemoveIcon sx={{ fontSize: 10 }} />
                          </IconButton>
                          <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, minWidth: 16, textAlign: "center", color: "#1e293b" }}>
                            {item.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => onUpdateQty(item.cartItemKey, 1)}
                            sx={{ width: 18, height: 18, border: "1px solid #e2e8f0", borderRadius: 0.5, "&:hover": { borderColor: "#3b82f6" } }}
                          >
                            <AddIcon sx={{ fontSize: 10 }} />
                          </IconButton>
                        </Stack>
                        <IconButton
                          className="cart-actions"
                          size="small"
                          onClick={() => onRemove(item.cartItemKey)}
                          sx={{ width: 16, height: 16, opacity: 0, transition: "opacity 0.15s", color: "#ef4444" }}
                        >
                          <CloseIcon sx={{ fontSize: 10 }} />
                        </IconButton>
                      </Stack>
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
        )
      })}
    </Box>
  )
}

// ─── Summary Tab ─────────────────────────────────────────────
const APPROVAL_STATUS = {
  draft: { icon: null, color: "#475569", bg: "#f1f5f9", label: "Draft" },
  pending_approval: { icon: HourglassTopIcon, color: "#92400e", bg: "#fef3c7", label: "Awaiting Approval" },
  approved: { icon: CheckCircleIcon, color: "#166534", bg: "#dcfce7", label: "Approved" },
  changes_requested: { icon: null, color: "#991b1b", bg: "#fee2e2", label: "Changes Requested" },
  revised: { icon: RateReviewIcon, color: "#1e40af", bg: "#dbeafe", label: "Revised by Specialist" },
  final_accepted: { icon: CheckCircleIcon, color: "#065f46", bg: "#d1fae5", label: "Final Accepted" },
}

const SummaryTab = ({
  cart,
  groupedCart,
  cartTotal,
  calculateFinalPrice,
  settingsChips,
  v5,
  onExportManufacturer,
  onExportCustomer,
  onExportSupplier,
  onSendForApproval,
  onFinalAccept,
  quoteStatus,
  reviewedBy,
  reviewedAt,
  t,
}) => {
  const hasItems = cart.length > 0
  const totalQty = cart.reduce((sum, i) => sum + i.quantity, 0)

  // Section breakdown
  const sectionBreakdown = []
  const cabinetGroups = groupedCart.groups["Cabinet"]
  if (cabinetGroups) {
    for (const [pos, items] of Object.entries(cabinetGroups)) {
      const qty = items.reduce((s, i) => s + i.quantity, 0)
      const subtotal = items.reduce((s, i) => s + calculateFinalPrice(parseFloat(i.b2bsalesprice) || 0) * i.quantity, 0)
      sectionBreakdown.push({
        label: SECTION_LABELS[pos] || pos,
        color: SECTION_COLORS[pos] || "#64748b",
        qty,
        subtotal,
      })
    }
  }
  // Accessories
  const accTotal = groupedCart.cat1Totals["Accessories"] || 0
  const accQty = (groupedCart.groups["Accessories"] ? Object.values(groupedCart.groups["Accessories"]).flat() : []).reduce((s, i) => s + i.quantity, 0)
  // Special
  const spcTotal = (groupedCart.cat1Totals["Special"] || 0) + (groupedCart.cat1Totals["Charges"] || 0)
  const spcQty = [...(groupedCart.groups["Special"] ? Object.values(groupedCart.groups["Special"]).flat() : []), ...(groupedCart.groups["Charges"] ? Object.values(groupedCart.groups["Charges"]).flat() : [])].reduce((s, i) => s + i.quantity, 0)

  // Prose description
  const prose = sectionBreakdown.map(s => `${s.qty} ${s.label.toLowerCase()}`).join(", ")

  return (
    <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
      {/* Settings recap */}
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 1 }}>
        {t("Ρυθμισεις")}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
        {settingsChips && settingsChips.map((chip, i) => (
          <Chip
            key={i}
            label={chip.label}
            size="small"
            sx={{ fontSize: "0.6rem", height: 22, bgcolor: "#f1f5f9", color: "#475569", fontWeight: 500 }}
          />
        ))}
        {(!settingsChips || settingsChips.length === 0) && (
          <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>{t("Δεν εχουν οριστει ρυθμισεις")}</Typography>
        )}
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Section breakdown */}
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 1 }}>
        {t("Αναλυση")}
      </Typography>

      {!hasItems ? (
        <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8", mb: 2 }}>
          {t("Δεν υπαρχουν προιοντα")}
        </Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {sectionBreakdown.map((s) => (
            <Stack key={s.label} direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 3, height: 12, bgcolor: s.color, borderRadius: 1 }} />
                <Typography sx={{ fontSize: "0.65rem", color: "#475569" }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>
                  ({s.qty} {t("τεμ.")})
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: s.color }}>
                €{s.subtotal.toFixed(2)}
              </Typography>
            </Stack>
          ))}
          {accQty > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 3, height: 12, bgcolor: "#d97706", borderRadius: 1 }} />
                <Typography sx={{ fontSize: "0.65rem", color: "#475569" }}>{t("Αξεσουαρ")}</Typography>
                <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>({accQty} {t("τεμ.")})</Typography>
              </Stack>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#d97706" }}>€{accTotal.toFixed(2)}</Typography>
            </Stack>
          )}
          {spcQty > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 3, height: 12, bgcolor: "#7c3aed", borderRadius: 1 }} />
                <Typography sx={{ fontSize: "0.65rem", color: "#475569" }}>{t("Ειδικα")}</Typography>
                <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>({spcQty} {t("τεμ.")})</Typography>
              </Stack>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#7c3aed" }}>€{spcTotal.toFixed(2)}</Typography>
            </Stack>
          )}
        </Stack>
      )}

      {/* Prose description */}
      {prose && (
        <Box sx={{ bgcolor: "#f8fafc", borderRadius: 1.5, p: 1.5, mb: 2 }}>
          <Typography sx={{ fontSize: "0.65rem", color: "#475569", lineHeight: 1.5 }}>
            {totalQty} {t("τεμ.")}: {prose}
            {accQty > 0 ? `, ${accQty} ${t("αξεσουαρ").toLowerCase()}` : ""}
            {spcQty > 0 ? `, ${spcQty} ${t("ειδικα").toLowerCase()}` : ""}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mb: 1.5 }} />

      {/* Export buttons */}
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 1 }}>
        {t("Εξαγωγη PDF")}
      </Typography>
      <Stack spacing={0.75}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<FactoryIcon sx={{ fontSize: 16 }} />}
          onClick={onExportManufacturer}
          disabled={!hasItems}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.72rem",
            justifyContent: "flex-start",
            borderColor: "#e2e8f0",
            color: "#475569",
            borderRadius: 1.5,
            "&:hover": { borderColor: "#e50019", color: "#e50019" },
          }}
        >
          {t("Κατασκευαστης")}
        </Button>
        <Tooltip
          title={quoteStatus !== "final_accepted" ? t("Customer PDF available after Final Accept") : ""}
          arrow
          placement="left"
        >
          <span>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={quoteStatus !== "final_accepted" ? <LockIcon sx={{ fontSize: 14 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
              onClick={onExportCustomer}
              disabled={!hasItems || quoteStatus !== "final_accepted"}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.72rem",
                justifyContent: "flex-start",
                borderColor: "#e2e8f0",
                color: quoteStatus === "final_accepted" ? "#475569" : "#94a3b8",
                borderRadius: 1.5,
                "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
              }}
            >
              {t("Πελατης")}
            </Button>
          </span>
        </Tooltip>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<LocalShippingIcon sx={{ fontSize: 16 }} />}
          onClick={onExportSupplier}
          disabled={!hasItems}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.72rem",
            justifyContent: "flex-start",
            borderColor: "#e2e8f0",
            color: "#475569",
            borderRadius: 1.5,
            "&:hover": { borderColor: "#16a34a", color: "#16a34a" },
          }}
        >
          {t("Προμηθευτης")}
        </Button>
      </Stack>

      {/* ── Approval / Status Section ── */}
      {(onSendForApproval || onFinalAccept) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", mb: 1 }}>
            {t("Approval")}
          </Typography>
          {quoteStatus && quoteStatus !== "draft" ? (
            <Box>
              {(() => {
                const st = APPROVAL_STATUS[quoteStatus] || APPROVAL_STATUS.draft
                const StatusIcon = st.icon
                return (
                  <Chip
                    icon={StatusIcon ? <StatusIcon sx={{ fontSize: 14 }} /> : undefined}
                    label={t(st.label)}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      height: 28,
                      bgcolor: st.bg,
                      color: st.color,
                      mb: 1,
                    }}
                  />
                )
              })()}

              {/* Specialist revision info */}
              {quoteStatus === "revised" && reviewedBy && (
                <Box sx={{ bgcolor: "#eff6ff", borderRadius: 1.5, p: 1.25, mb: 1, border: "1px solid #bfdbfe" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#1e40af", mb: 0.25 }}>
                    {t("Reviewed by")}: {reviewedBy}
                  </Typography>
                  {reviewedAt && (
                    <Typography sx={{ fontSize: "0.58rem", color: "#3b82f6" }}>
                      {new Date(reviewedAt).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Final Accept button — visible when specialist has approved or revised */}
              {(quoteStatus === "revised" || quoteStatus === "approved") && onFinalAccept && (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  startIcon={<ThumbUpIcon sx={{ fontSize: 14 }} />}
                  onClick={onFinalAccept}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    bgcolor: "#059669",
                    borderRadius: 1.5,
                    py: 0.75,
                    mb: 0.75,
                    "&:hover": { bgcolor: "#047857" },
                  }}
                >
                  {t("Final Accept")}
                </Button>
              )}

              {/* Re-submit when changes requested */}
              {quoteStatus === "changes_requested" && onSendForApproval && (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                  onClick={onSendForApproval}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    bgcolor: "#d97706",
                    borderRadius: 1.5,
                    "&:hover": { bgcolor: "#b45309" },
                  }}
                >
                  {t("Re-submit for Approval")}
                </Button>
              )}

              {/* Final accepted — show print-ready message */}
              {quoteStatus === "final_accepted" && (
                <Box sx={{ bgcolor: "#d1fae5", borderRadius: 1.5, p: 1.25, border: "1px solid #a7f3d0" }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#065f46" }}>
                    {t("Quote accepted — Customer PDF is now available")}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : onSendForApproval ? (
            <Button
              fullWidth
              size="small"
              variant="contained"
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              onClick={onSendForApproval}
              disabled={!hasItems}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                bgcolor: "#d97706",
                borderRadius: 1.5,
                py: 0.75,
                "&:hover": { bgcolor: "#b45309" },
              }}
            >
              {t("Send for Approval")}
            </Button>
          ) : null}
        </>
      )}
    </Box>
  )
}

// ─── Main EnhancedSummaryPanel ──────────────────────────────
const EnhancedSummaryPanel = ({
  cart,
  groupedCart,
  cartTotal,
  ralSurcharge,
  quoteDiscount = 0,
  onQuoteDiscountChange,
  discountAmount = 0,
  netAmount,
  vatRate = 24,
  vatAmount = 0,
  grandTotal,
  extraCharge = 0,
  onExtraChargeChange,
  extraChargeAmount = 0,
  calculateFinalPrice,
  onUpdateQty,
  onRemove,
  settingsChips,
  v5,
  onExportManufacturer,
  onExportCustomer,
  onExportSupplier,
  onSendForApproval,
  onFinalAccept,
  quoteStatus,
  reviewedBy,
  reviewedAt,
}) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)

  const hasItems = cart.length > 0
  const totalQty = cart.reduce((sum, i) => sum + i.quantity, 0)

  return (
    <Box
      sx={{
        width: 300,
        minWidth: 280,
        borderLeft: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderRadius: { xs: 0, md: "0 8px 8px 0" },
      }}
    >
      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          borderBottom: "1px solid #e2e8f0",
          "& .MuiTab-root": {
            minHeight: 36,
            fontSize: "0.72rem",
            fontWeight: 700,
            textTransform: "none",
            py: 0.5,
          },
          "& .Mui-selected": { color: "#e50019" },
          "& .MuiTabs-indicator": { bgcolor: "#e50019" },
        }}
      >
        <Tab
          icon={<ShoppingCartIcon sx={{ fontSize: 14 }} />}
          iconPosition="start"
          label={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <span>{t("Καλαθι")}</span>
              {totalQty > 0 && (
                <Chip label={totalQty} size="small" sx={{ height: 16, fontSize: "0.55rem", fontWeight: 700, bgcolor: "#fde8ea", color: "#9a0011" }} />
              )}
            </Stack>
          }
        />
        <Tab
          icon={<SummarizeIcon sx={{ fontSize: 14 }} />}
          iconPosition="start"
          label={t("Συνοψη")}
        />
      </Tabs>

      {/* Tab content */}
      {activeTab === 0 && (
        <CartTab
          cart={cart}
          groupedCart={groupedCart}
          cartTotal={cartTotal}
          calculateFinalPrice={calculateFinalPrice}
          onUpdateQty={onUpdateQty}
          onRemove={onRemove}
          t={t}
        />
      )}
      {activeTab === 1 && (
        <SummaryTab
          cart={cart}
          groupedCart={groupedCart}
          cartTotal={cartTotal}
          calculateFinalPrice={calculateFinalPrice}
          settingsChips={settingsChips}
          v5={v5}
          onExportManufacturer={onExportManufacturer}
          onExportCustomer={onExportCustomer}
          onExportSupplier={onExportSupplier}
          onSendForApproval={onSendForApproval}
          onFinalAccept={onFinalAccept}
          quoteStatus={quoteStatus}
          reviewedBy={reviewedBy}
          reviewedAt={reviewedAt}
          t={t}
        />
      )}

      {/* Sticky cost-breakdown footer */}
      {(() => {
        const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d)
        const rawTotal = cart.reduce((sum, i) => sum + (parseFloat(i.b2bsalesprice) || 0) * i.quantity, 0)
        const isCartView = activeTab === 0
        const ralTotal = num(ralSurcharge?.total)
        const ralCount = ralSurcharge?.codes?.length || 0
        const safeExtra = num(extraChargeAmount) || num(extraCharge)
        // Cart tab shows raw (pre-margin) preview + extra; Summary tab uses
        // cartTotal (already includes extra + RAL from useStoreLogic).
        const preDiscount = isCartView ? rawTotal + ralTotal + safeExtra : num(cartTotal)
        const safeDiscount = num(discountAmount)
        const safeVatRate = num(vatRate, 24)
        const effectiveNet = netAmount != null ? num(netAmount) : Math.max(0, preDiscount - safeDiscount)
        const effectiveVat = num(vatAmount) || effectiveNet * (safeVatRate / 100)
        const effectiveGrand = grandTotal != null ? num(grandTotal) : effectiveNet + effectiveVat
        return (
          <Box
            sx={{
              borderTop: "2px solid #e2e8f0",
              px: 1.5,
              py: 1,
              bgcolor: "#f8fafc",
            }}
          >
            {/* Extra Χρέωση — free-form flat charge added on top of the cart. */}
            {onExtraChargeChange && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#64748b" }}>
                  {t("Extra Χρεωση")}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={extraCharge || ""}
                  onChange={e => {
                    const v = Math.max(0, Number(e.target.value) || 0)
                    onExtraChargeChange(v)
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start" sx={{ "& p": { fontSize: "0.65rem" } }}>€</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 1, style: { padding: "3px 6px", fontSize: "0.72rem", fontWeight: 700, textAlign: "right" } }}
                  sx={{ width: 96 }}
                />
              </Stack>
            )}
            {ralCount > 0 && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#d97706" }}>
                  {t("RAL surcharge")} ({ralCount} × €{ralSurcharge.perCode})
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#d97706" }}>
                  €{ralTotal.toFixed(2)}
                </Typography>
              </Stack>
            )}
            {/* Discount input — quote-level %, applies before VAT */}
            {onQuoteDiscountChange && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#64748b" }}>
                  {t("Εκπτωση")}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={quoteDiscount || ""}
                  onChange={e => {
                    const v = Math.max(0, Math.min(100, Number(e.target.value) || 0))
                    onQuoteDiscountChange(v)
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end" sx={{ "& p": { fontSize: "0.65rem" } }}>%</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 100, step: 1, style: { padding: "3px 6px", fontSize: "0.72rem", fontWeight: 700, textAlign: "right" } }}
                  sx={{ width: 76 }}
                />
              </Stack>
            )}
            {safeDiscount > 0 && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#16a34a" }}>
                  {t("Εκπτωση")} −{num(quoteDiscount).toFixed(0)}%
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#16a34a" }}>
                  −€{safeDiscount.toFixed(2)}
                </Typography>
              </Stack>
            )}
            {/* Net (post-discount, pre-VAT) */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#64748b" }}>
                {t("Καθαρη αξια")}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1e293b" }}>
                €{effectiveNet.toFixed(2)}
              </Typography>
            </Stack>
            {/* VAT */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#64748b" }}>
                {t("ΦΠΑ")} {safeVatRate.toFixed(0)}%
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1e293b" }}>
                €{effectiveVat.toFixed(2)}
              </Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                {t("Πληρωτεο")}
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#1e293b" }}>
                €{effectiveGrand.toFixed(2)}
              </Typography>
            </Stack>
          </Box>
        )
      })()}
    </Box>
  )
}

export default EnhancedSummaryPanel
